/**
 * Card 1 — "the subscription", per design_handoff_billing. Pure rendering:
 * state (badge/band/facts/actions copy) comes from planSummaryState.js,
 * this component only maps the resulting action keys onto the callbacks
 * BillingPage supplies (which own the Stripe redirects/redux dispatches).
 */
import React, { useMemo } from "react";
import Card from "../../theme/untitled/Card/Card";
import Badge from "../../theme/untitled/Badge/Badge";
import Button from "../../theme/untitled/Button/Button";
import Icon from "../../theme/untitled/icons/Icon";
import Band from "./Band";
import { useCurrentOrganizationStore } from "../../../../hooks/redux/currentOrganization";
import { useUserStore } from "../../../../hooks/redux/user";
import { getPlanSummaryState } from "./planSummaryState";
import styles from "./PlanSummaryCard.module.scss";

const ExtIcon = () => (
  <Icon name="ArrowUpRight" size={14} className={styles.extIcon} />
);

/**
 * @param {Object} actions - key -> function, one per action key used by planSummaryState
 * @param {boolean} disabled - true while a Stripe session/link fetch is in flight
 * @param {boolean=} checkingOut - true right after returning from Stripe
 *   Checkout, forces the "payment processing" state until the webhook lands
 */
const PlanSummaryCard = ({
  actions,
  disabled = false,
  checkingOut = false,
}) => {
  const { currentOrganization } = useCurrentOrganizationStore();
  const { user } = useUserStore();
  const accountHasUsedTrial = Boolean(user.model?.has_used_trial);
  const state = useMemo(
    () => getPlanSummaryState(currentOrganization, checkingOut, accountHasUsedTrial),
    [currentOrganization, checkingOut, accountHasUsedTrial]
  );

  // One stable handler per action key, keyed up front so mapped/nested
  // elements (facts, primary actions, footer links, band action) can all
  // pull a stable reference instead of closing over `key` inline.
  const actionHandlers = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(actions).map((key) => [
          key,
          (e) => {
            e?.preventDefault?.();
            if (disabled) return;
            actions[key]?.();
          },
        ])
      ),
    [actions, disabled]
  );

  const bandAction = useMemo(
    () =>
      state.band?.action && {
        label: state.band.action.label,
        disabled,
        onClick: actionHandlers[state.band.action.key],
      },
    [state.band, disabled, actionHandlers]
  );

  const factRows = state.facts.map((fact) => (
    <React.Fragment key={fact.label}>
      <dt>{fact.label}</dt>
      <dd>
        <span className={styles.factValue}>{fact.value}</span>
        {fact.action && (
          <a href="#" onClick={actionHandlers[fact.action]}>
            Edit
          </a>
        )}
      </dd>
    </React.Fragment>
  ));

  const facts = factRows.length ? (
    <dl className={styles.facts}>{factRows}</dl>
  ) : null;

  const primaryActionButtons = state.primaryActions.map((action) => (
    <Button
      key={action.key}
      hierarchy={action.hierarchy}
      disabled={disabled || action.disabled}
      iconTrailing={action.external ? <ExtIcon /> : null}
      onClick={actionHandlers[action.key]}
    >
      {action.label}
    </Button>
  ));

  const footerLinkButtons = state.footerLinks.map((link, idx) => (
    <React.Fragment key={link.key}>
      {idx > 0 && <span className={styles.sep}>·</span>}
      <button
        type="button"
        className={styles.link}
        disabled={disabled}
        onClick={actionHandlers[link.key]}
      >
        {link.label}
        {link.external && <ExtIcon />}
      </button>
    </React.Fragment>
  ));
  const footerContent = state.footerNote ? (
    <span>{state.footerNote}</span>
  ) : (
    footerLinkButtons
  );

  return (
    <Card>
      <Card.Header
        title={
          <span className={styles.titleRow}>
            {state.title}
            <Badge color={state.badge.color} size="sm">
              {state.badge.label}
            </Badge>
          </span>
        }
        subtitle={state.subtitle}
      />
      {state.band && (
        <Band
          kind={state.band.kind}
          icon={<Icon name={state.band.icon} size={20} />}
          title={state.band.title}
          action={bandAction}
        >
          {state.band.body}
        </Band>
      )}
      <Card.Body className={styles.body}>
        {facts}
        <div className={styles.actions}>{primaryActionButtons}</div>
      </Card.Body>
      {(state.footerLinks.length > 0 || state.footerNote) && (
        <Card.Footer className={styles.footer}>{footerContent}</Card.Footer>
      )}
    </Card>
  );
};

export default PlanSummaryCard;
